"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormStatus, SubmitButton } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { cUserAccountsTranslations } from "./CUserAccounts.i18n";
import { MfaCodeInput } from "./MfaCodeInput";
import modalStyles from "./AvatarUploadModal.module.css";
import styles from "./AuthMfaModal.module.css";

/**
 * AuthMfaModal - drugi krok logowania z 2FA. Otwierany przez LoginForm gdy
 * `POST /api/iiapi/login` zwrocilo `{mfa_required: true}` (session cookie
 * `imperial_mfa_challenge` juz ustawiony przez route handler).
 *
 * Struktura zgodna z ChangeEmailModal/DeleteAccountModal - portal do body,
 * backdrop click zamyka (z drag-guard), Escape zamyka, style z shared
 * AvatarUploadModal.module.css + kilka lokalnych.
 *
 * Kod wpisywany przez MfaCodeInput (6 osobnych pol cyfrowych, auto-focus
 * next, paste 6-cyfr rozpraszany, auto-submit po pelnym kodzie). Reuse
 * z ProfileTabSecurity.
 *
 * Rate limit + TTL 5 min sa po stronie backendu (300117/300116) - modal
 * pokazuje komunikat i wywoluje `onCancel()` zeby parent (LoginForm) mogl
 * wyswietlic user'owi login form od nowa.
 *
 * **onSuccess payload** - dokladnie to co wraca z `/api/iiapi/login/mfa`
 * (analog `/api/iiapi/login` po pomyslnym loginie). Parent wykonuje ta sama
 * logike post-login co dla direct login (SSO plant, redirect, refresh).
 */

export type AuthMfaSuccessResult = {
  user: unknown;
  remember_me: boolean;
  plant?: { ticket: string; action: string };
};

export function AuthMfaModal({
  onSuccess,
  onCancel,
}: {
  /** Wywolane po pomyslnej weryfikacji kodu - parent wykonuje flow post-login
   *  (SSO plant, redirect, router.refresh). */
  onSuccess: (result: AuthMfaSuccessResult) => void;
  /** Wywolane gdy user zamyka modal (klik "Anuluj", backdrop, Escape) lub
   *  gdy backend zwroci terminal error (TTL/rate limit) - parent zamyka modal
   *  i pokazuje login form od nowa. Zawsze idzie w parze z POST na
   *  `/api/iiapi/login/mfa/cancel` (kasuje session state + cookie). */
  onCancel: () => void;
}) {
  const t = useT(cUserAccountsTranslations);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const backdropMouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => setMounted(true), []);

  //POST cancel + zamkniecie w jednej akcji - callowane z close button/backdrop/Escape.
  //Fire-and-forget cancel na backendzie (route handler sam kasuje cookie i wraca
  //200 nawet gdy backend niedostepny).
  const cancelAndClose = useCallback(() => {
    void fetch("/api/iiapi/login/mfa/cancel", { method: "POST" });
    onCancel();
  }, [onCancel]);

  //Esc zamyka gdy nie busy.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) cancelAndClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, cancelAndClose]);

  const submitCode = useCallback(
    async (value: string) => {
      if (!/^\d{6}$/.test(value) || busy) return;
      setBusy(true);
      setError(null);
      try {
        const res = await fetch("/api/iiapi/login/mfa", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: value }),
        });
        const body = (await res.json().catch(() => null)) as
          | ({
              ok?: boolean;
              user?: unknown;
              remember_me?: boolean;
              plant?: { ticket: string; action: string };
              message?: string;
              code?: number;
            } | null);

        if (!res.ok || body?.ok !== true) {
          //Terminal errors z backendu (TTL wygaslo / rate limit / brak sesji)
          //-> route handler juz skasowal challenge cookie, parent zamyka modal
          //i pokazuje login form. Reszta bledow (zly kod) -> zostajemy w modalu.
          const backendCode = body?.code;
          const isTerminal =
            backendCode === 300115 || backendCode === 300116 || backendCode === 300117 || backendCode === 300119;
          if (isTerminal) {
            setError(body?.message ?? t.authMfaModal.sessionExpired);
            setBusy(false);
            //Krotkie opoznienie zeby user zobaczyl komunikat przed zamknieciem.
            window.setTimeout(() => onCancel(), 1200);
            return;
          }
          //Non-terminal - user moze poprawic kod. Czyscimy pola przez re-mount
          //MfaCodeInput (parent state `code` = ""), pokazujemy error.
          setCode("");
          setError(body?.message ?? t.authMfaModal.invalidCode);
          setBusy(false);
          return;
        }

        onSuccess({
          user: body.user,
          remember_me: !!body.remember_me,
          plant: body.plant,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setBusy(false);
      }
    },
    [busy, onCancel, onSuccess],
  );

  //Auto-submit gdy user wpisze wszystkie 6 cyfr. Powtorka logic z admin login
  //MFA challenge (login_page_mfa.imp).
  useEffect(() => {
    if (code.length === 6 && !busy) {
      void submitCode(code);
    }
  }, [code, busy, submitCode]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={modalStyles.backdrop}
      onMouseDown={(e) => {
        backdropMouseDownTargetRef.current = e.target;
      }}
      onClick={(e) => {
        if (
          e.target === e.currentTarget &&
          backdropMouseDownTargetRef.current === e.currentTarget &&
          !busy
        ) {
          cancelAndClose();
        }
        backdropMouseDownTargetRef.current = null;
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t.authMfaModal.ariaLabel}
    >
      <div className={modalStyles.modal}>
        <header className={modalStyles.header}>
          <h2 className={modalStyles.title}>{t.authMfaModal.title}</h2>
          <button
            type="button"
            className={modalStyles.closeBtn}
            onClick={cancelAndClose}
            disabled={busy}
            aria-label={t.authMfaModal.close}
          >
            <Icon name="x" />
          </button>
        </header>

        <div className={modalStyles.body}>
          <p className={styles.intro}>{t.authMfaModal.intro}</p>

          <MfaCodeInput
            value={code}
            onChange={setCode}
            onComplete={(v) => void submitCode(v)}
            disabled={busy}
            hasError={Boolean(error)}
            autoFocus
          />

          {error && <FormStatus variant="error">{error}</FormStatus>}

          <div className={styles.actions}>
            <SubmitButton
              type="button"
              loading={busy}
              disabled={code.length !== 6}
              onClick={() => void submitCode(code)}
            >
              {t.authMfaModal.verify}
            </SubmitButton>
            <SubmitButton
              type="button"
              variant="link"
              disabled={busy}
              onClick={cancelAndClose}
            >
              {t.authMfaModal.cancel}
            </SubmitButton>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
